import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import UnitCard from "@/components/common/UnitCard";
import { Spinner, EmptyState } from "@/components/common/Primitives";
import { useUnits } from "@/hooks/useSiteQueries";
import { formatUnitCount } from "@/lib/unitCountDisplay";

export default function UnitsPage() {
  const { data: allUnits, isPending } = useUnits();
  const [query, setQuery] = useState("");
  const [deanery, setDeanery] = useState("All");

  const deaneries = useMemo(() => {
    if (!allUnits) return ["All"];
    return ["All", ...Array.from(new Set(allUnits.map((u) => u.deanery).filter(Boolean)))];
  }, [allUnits]);

  const units = useMemo(() => {
    if (!allUnits) return null;
    const q = query.toLowerCase();
    return allUnits.filter((u) => {
      const matchQ =
        !q ||
        u.name?.toLowerCase().includes(q) ||
        u.parish_name?.toLowerCase().includes(q) ||
        u.area?.toLowerCase().includes(q) ||
        u.deanery?.toLowerCase().includes(q);
      const matchD = !deanery || deanery === "All" || u.deanery === deanery;
      return matchQ && matchD;
    });
  }, [allUnits, query, deanery]);

  return (
    <div data-testid="units-page">
      <section className="bg-maroon text-white">
        <div className="container-bcs py-20 sm:py-24">
          <p className="eyebrow !text-gold-light mb-4">My Parish · My Unit</p>
          <h1 className="heading-xl !text-white max-w-3xl">
            Find your <span className="italic text-gold-light">BCS unit</span>
          </h1>
          <p className="mt-6 text-white/80 max-w-2xl text-lg leading-relaxed">
            {allUnits?.length
              ? `${formatUnitCount(allUnits.length)} parish-based units across every deanery of the Archdiocese — search by name, parish or area.`
              : "Parish-based units across every deanery of the Archdiocese — search by name, parish or area."}
          </p>
        </div>
      </section>

      <section className="container-bcs py-14 sm:py-16">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              className="input-bcs pl-11"
              placeholder="Search by unit, parish or area…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="units-search-input"
            />
          </div>
          <select
            className="input-bcs sm:w-52"
            value={deanery}
            onChange={(e) => setDeanery(e.target.value)}
            data-testid="units-deanery-filter"
          >
            {deaneries.map((d) => (
              <option key={d} value={d}>
                {d === "All" ? "All deaneries" : d}
              </option>
            ))}
          </select>
        </div>

        {isPending ? (
          <Spinner className="py-16" />
        ) : units.length === 0 ? (
          <EmptyState title="No units found" message="Try a different search or deanery filter." />
        ) : (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {units.map((u) => (
              <UnitCard key={u.slug} unit={u} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
